import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from '@/libs/router'
import type { OverviewInsight } from '../domain/overview'

const TICK_DURATION = 6000

const linkClass = 'border-b border-dashed border-878787/30 hover:text-foreground transition-colors'

type SummaryTickerProps = {
	insights: OverviewInsight[]
}

const SummaryTicker = ({ insights }: SummaryTickerProps) => {
	const [index, setIndex] = useState(0)
	const [direction, setDirection] = useState(1)
	const [fast, setFast] = useState(false)
	const [progress, setProgress] = useState(0)
	const hoveredRef = useRef(false)
	const startRef = useRef(Date.now())
	const elapsedOnPauseRef = useRef(0)

	// Reset to first slide whenever the insight set changes shape (e.g. data loads in)
	useEffect(() => {
		setIndex(0)
		setDirection(1)
		setFast(false)
		setProgress(0)
		startRef.current = Date.now()
		elapsedOnPauseRef.current = 0
	}, [insights.length])

	const goTo = useCallback(
		(i: number) => {
			const next = i % insights.length
			if (next === index) return
			setFast(true)
			setDirection(next > index ? 1 : -1)
			setIndex(next)
			setProgress(0)
			startRef.current = Date.now()
			elapsedOnPauseRef.current = 0
		},
		[insights.length, index],
	)

	useEffect(() => {
		if (insights.length <= 1) return

		let raf: number

		const tick = () => {
			if (hoveredRef.current) {
				raf = requestAnimationFrame(tick)
				return
			}

			const elapsed = elapsedOnPauseRef.current + (Date.now() - startRef.current)
			const pct = Math.min(elapsed / TICK_DURATION, 1)
			setProgress(pct)

			if (pct >= 1) {
				setFast(false)
				setDirection(1)
				setIndex(prev => (prev + 1) % insights.length)
				setProgress(0)
				startRef.current = Date.now()
				elapsedOnPauseRef.current = 0
			}

			raf = requestAnimationFrame(tick)
		}

		raf = requestAnimationFrame(tick)
		return () => cancelAnimationFrame(raf)
	}, [insights.length])

	const current = insights[index % insights.length]!
	const slideY = (fast ? 8 : 14) * direction
	const duration = fast ? 0.12 : 0.32

	if (insights.length <= 1) {
		return (
			<p className='text-muted-foreground text-sm mt-3 max-w-lg leading-relaxed text-center'>
				{current.before}
				{current.link && (
					<Link to={current.href} className={linkClass}>
						{current.link}
					</Link>
				)}
				{current.after}
			</p>
		)
	}

	return (
		<div
			className='flex flex-col items-center max-w-lg w-full gap-3'
			onMouseEnter={() => {
				hoveredRef.current = true
				elapsedOnPauseRef.current += Date.now() - startRef.current
			}}
			onMouseLeave={() => {
				hoveredRef.current = false
				startRef.current = Date.now()
			}}
		>
			<div className='relative h-10 overflow-hidden w-full'>
				<AnimatePresence mode='wait' initial={false} custom={direction}>
					<motion.div
						key={current.key}
						className='absolute inset-0 flex items-center justify-center'
						initial={{ y: slideY, opacity: 0, filter: 'blur(4px)' }}
						animate={{ y: 0, opacity: 1, filter: 'blur(0px)' }}
						exit={{ y: -slideY, opacity: 0, filter: 'blur(4px)' }}
						transition={{ duration, ease: [0.16, 1, 0.3, 1] }}
					>
						<span className='text-muted-foreground text-sm leading-relaxed text-center'>
							{current.before}
							{current.link && (
								<Link to={current.href} className={linkClass}>
									{current.link}
								</Link>
							)}
							{current.after}
						</span>
					</motion.div>
				</AnimatePresence>
			</div>

			<div className='flex items-center gap-1.5'>
				{insights.map((insight, i) => (
					<button
						key={insight.key}
						type='button'
						className='group/bar relative cursor-pointer py-2 -my-2'
						onClick={() => goTo(i)}
						onMouseEnter={() => goTo(i)}
					>
						<div className='relative h-[2px] w-4 rounded-full bg-primary/10 overflow-hidden'>
							<div
								className='absolute inset-0 bg-primary/40 rounded-full origin-left group-hover/bar:!scale-x-100'
								style={{ transform: `scaleX(${i === index ? progress : 0})` }}
							/>
						</div>
					</button>
				))}
			</div>
		</div>
	)
}

export { SummaryTicker }
